import { Component, Element, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-risks';

/**
 * Risks / penalties callout highlighting compliance or legal warnings.
 * Light red background card with a heading above a 2-column grid of warning-icon items; each item shows a bold label followed by description text.
 * Alert-styled risk emphasis with warning icons, unlike neutral requirements or feature variants.
 *
 * @category SignNow
 * @slot heading - <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]>
 * @example
 *   <sn-risks
 *     items='[{"label":"Legal Invalidity:","text":"Documents signed without proper authentication may be challenged in court."},{"label":"Data Breaches:","text":"Unencrypted signatures expose sensitive personal information."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Penalties / Risks</me-heading>
 *   </sn-risks>
 */
@Component({
  tag: 'sn-risks',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcPenaltiesRisks {
  @Element() el: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Warning items with a bold label and description text.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Bold label text
   * @item.property {string} text - Description text
   * @item.required text
   * @item.content label text
   * @minItems 4
   */
  @Prop({ mutable: true }) items?: { label?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {array} items - Fallback items (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        raw_html?: string;
        items?: { label?: string; text?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__wrapper`}>
          <slot name="heading" />
          {items.length > 0 && (
            <div class={`${COMPONENT_NAME}__grid`}>
              {items.map(item => (
                <div class={`${COMPONENT_NAME}__item`}>
                  <me-icon name="alerts--error-circle" class={`${COMPONENT_NAME}__icon`} width="20" height="20" lazy={false}></me-icon>
                  <me-text tag="div">
                    {item.label && <me-heading tag="h3" class={`${COMPONENT_NAME}__r-title`}>{item.label}</me-heading>}
                    {item.text ? ` ${item.text}` : ''}
                  </me-text>
                </div>
              ))}
            </div>
          )}
        </div>
      </me-section>
    );
  }
}
